import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ClaimPolicyVisibility } from '../../../models/api/reports/claim-policy-filter';
import {
  FilterCriteria,
  FilterDetail,
  FilterSectionType,
} from '../../../models/api/reports/filter-detail-response';
import { FilterGroupVisibility } from '../../../models/api/reports/filter-security';
import { CasePipe } from '../../../pipes/case.pipe';
import { FiltersService } from '../../../services/filters/filters.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { BaseComponent } from '../../base/base.component';
import { AddRemoveButtonComponent } from '../../core/buttons/add-remove-button.component';
import { InputFieldComponent } from '../../core/fields/input-field.component';
import { CountLabelComponent } from '../../core/labels/count-label.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { CardComponent } from '../../core/panels/card.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { AgencyCriteriaComponent } from './criteria/agency-criteria.component';
import { ClaimFlagCriteriaComponent } from './criteria/claim-flag-criteria.component';
import { ClaimNumberCriteriaComponent } from './criteria/claim-number-criteria.component';
import { ClaimPolicyCriteriaComponent } from './criteria/claim-policy-criteria.component';
import { ClientFieldCriteriaComponent } from './criteria/client-field-criteria.component';
import { DateCriteriaComponent } from './criteria/date-criteria.component';
import { ExaminerCriteriaComponent } from './criteria/examiner-criteria.component';
import { FinancialCriteriaComponent } from './criteria/financial-criteria.component';
import { LagTimeCriteriaComponent } from './criteria/lag-time-criteria.component';
import { LobCriteriaComponent } from './criteria/lob-criteria.component';
import { LossCodeCriteriaComponent } from './criteria/loss-code-criteria.component';
import { PolicyCriteriaComponent } from './criteria/policy-criteria.component';
import { StateCriteriaComponent } from './criteria/state-criteria.component';
import { StatusCriteriaComponent } from './criteria/status-criteria.component';
import { WorkStatusCriteriaComponent } from './criteria/work-status-criteria.component';
import { LocationCriteriaComponent } from './criteria/location-criteria.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-edit-filter',
  imports: [
    PageHeaderComponent,
    CountLabelComponent,
    CardComponent,
    EmptyStateComponent,
    StateCriteriaComponent,
    ClaimFlagCriteriaComponent,
    ClaimPolicyCriteriaComponent,
    ExaminerCriteriaComponent,
    LossCodeCriteriaComponent,
    PolicyCriteriaComponent,
    ClaimNumberCriteriaComponent,
    AgencyCriteriaComponent,
    ClientFieldCriteriaComponent,
    StatusCriteriaComponent,
    LobCriteriaComponent,
    LagTimeCriteriaComponent,
    WorkStatusCriteriaComponent,
    DateCriteriaComponent,
    FinancialCriteriaComponent,
    LocationCriteriaComponent,
    InputFieldComponent,
    AddRemoveButtonComponent,
    LinkLabelComponent,
    CasePipe,
    LoadingPanelComponent,
  ],
  template: `
    <div class="tw-pb-8 tw-min-w-0 tw-w-full">
      <div class="tw-pb-4">
        <div class="tw-flex tw-flex-col tw-gap-0 tw-w-full">
          <app-input-field
            [control]="fg.controls.filterName"
            label="Filter name"
            helpText="Enter name for the new filter"
            labelClasses="tw-basis-full md:tw-basis-[30%]"
            valueClasses="tw-basis-full md:tw-basis-[70%] md:tw-max-w-[400px]"
            [maxlength]="100"
          />
        </div>
      </div>

      <div class="tw-pb-8">
        <app-page-header label="Filter criteria" headerSize="h2" />
        <app-loading-panel
          [state]="loading ? 'Loading' : 'Loaded'"
          [error]="error"
        >
          <ng-template #loaded>
            <div
              class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-min-w-0 tw-w-full"
            >
              <nav
                aria-label="Filter criteria"
                class="sm:tw-min-w-[200px] sm:tw-shrink-0 sm:tw-pr-4 sm:tw-border-r"
              >
                <div
                  class="tw-flex tw-flex-col tw-gap-2 md:tw-sticky md:tw-top-[100px] md:tw-max-h-[calc(100vh-140px)] md:tw-overflow-y-auto"
                >
                  @for (section of sections(); track section) {
                    <div class="tw-flex tw-items-center tw-justify-between">
                      <div class="tw-flex tw-items-center">
                        <app-add-remove-button
                          [hasValue]="section.enabled()"
                          (click)="toggleEnabled(section)"
                          [addAriaLabel]="'Add ' + section.label"
                          [removeAriaLabel]="'Delete ' + section.label"
                        />
                        @if (section.enabled()) {
                          <app-link-label
                            [anchor]="section.label | case: 'kebab'"
                          >
                            {{ section.label }}
                          </app-link-label>
                        } @else {
                          <div>{{ section.label }}</div>
                        }
                      </div>

                      @if (section.enabled()) {
                        <app-count-label
                          [label]="
                            section.count() === 0 ? null : section.count()
                          "
                        />
                      }
                    </div>
                  }
                </div>
              </nav>
              <div class="tw-grow tw-flex tw-flex-col tw-gap-4 tw-min-w-0">
                @if (filtersEmpty()) {
                  <div class="">
                    <app-empty-state
                      icon="fa-light fa-filter"
                      label="None selected"
                      message="Select at least one criteria for this filter"
                      height="506px"
                      backgroundSize="contain"
                      backgroundImage="url('/assets/images/MovementArrow.webp')"
                    />
                  </div>
                }
                @for (section of sections(); track section) {
                  @if (section.enabled()) {
                    <div
                      [id]="section.label | case: 'kebab'"
                      class="tw-min-w-0"
                      style="scroll-margin-top: 150px"
                    >
                      <app-card
                        type="Medium"
                        [label]="section.label"
                        [headingLevel]="3"
                        labelCase="none"
                        minHeight="100px"
                      >
                        @switch (section.type) {
                          @case ('State') {
                            <app-state-criteria [section]="section" />
                          }
                          @case ('Date') {
                            <app-date-criteria [section]="section" />
                          }
                          @case ('Financial') {
                            <app-financial-criteria [section]="section" />
                          }
                          @case ('Flag') {
                            <app-claim-flag-criteria [section]="section" />
                          }
                          @case ('Examiner') {
                            <app-examiner-criteria [section]="section" />
                          }
                          @case ('Loss') {
                            <app-loss-code-criteria [section]="section" />
                          }
                          @case ('Policy and plan') {
                            <app-policy-criteria [section]="section" />
                          }
                          @case ('Claim Number') {
                            <app-claim-number-criteria [section]="section" />
                          }
                          @case ('Agency') {
                            <app-agency-criteria [section]="section" />
                          }
                          @case ('Claim Policy') {
                            <app-claim-policy-criteria
                              [section]="section"
                              [visibility]="claimPolicyVisibility"
                            />
                          }
                          @case ('Client Field') {
                            <app-client-field-criteria [section]="section" />
                          }
                          @case ('Status') {
                            <app-status-criteria [section]="section" />
                          }
                          @case ('Line of business') {
                            <app-lob-criteria [section]="section" />
                          }
                          @case ('Lag Time') {
                            <app-lag-time-criteria [section]="section" />
                          }
                          @case ('Work Status') {
                            <app-work-status-criteria [section]="section" />
                          }
                          @case ('Location') {
                            <app-location-criteria
                              [section]="section"
                              [filterGroupVisibility]="filterGroupVisibility()"
                              [showCorporateStructure]="
                                !filterGroupVisibility()
                                  ?.isCorpStructRestricted &&
                                (filterGroupVisibility()?.showLocations ?? true)
                              "
                            />
                          }
                        }
                      </app-card>
                    </div>
                  }
                }
              </div>
            </div>
          </ng-template>
        </app-loading-panel>
      </div>
    </div>
  `,
  styles: ``,
})
export class EditFilterComponent extends BaseComponent implements OnInit {
  private reports = inject(ReportsService);
  private filtersService = inject(FiltersService);

  @Input()
  filterDetail: FilterDetail;

  @Input()
  isCopyMode = false;

  error: any = null;
  loading = false;
  initialized = false;

  sections = signal<FilterSection[]>([]);
  claimPolicyVisibility: ClaimPolicyVisibility;
  filterGroupVisibility = signal<FilterGroupVisibility | null>(null);

  fg = new FormGroup({
    filterName: new FormControl<string>(null, [
      Validators.required,
      ValidatorsEx.alphaNumericWith(
        '/-_ ',
        'Slashes (/), dashes (-), and underscores (_) are the only special characters allowed in filter name.',
      ),
      this.filterNameExistsValidator(),
    ]),
  });

  filtersEmpty = computed(() => {
    for (const section of this.sections()) {
      if (section.enabled()) {
        return false;
      }
    }

    return true;
  });

  hasAnyCriteria = computed(() =>
    this.sections().some((s) => s.enabled() && s.count() > 0),
  );

  constructor() {
    super();
    effect(() => {
      const client = this.reports.selectedClient();
      if (client && this.sections().length && this.initialized) {
        this.updateDynamicSections(client);
      }
    });
  }

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    const contractNumber = this.reports.selectedClient() || undefined;

    const [, groupVisibility, claimPolicyVisibility] = await Promise.all([
      this.filtersService.loadUserFilters('JURIS', 'CLAIM', null),
      this.api.filtersCriteria.getFilterGroupVisibility(contractNumber),
      this.api.filtersCriteria.getClaimPolicyVisibility(contractNumber),
    ]);

    this.filterGroupVisibility.set(groupVisibility);

    // Create all sections
    const allSections = [
      this.createSection('Locations', 'Location'),
      this.createSection('Claim status', 'Status'),
      this.createSection('Line of business', 'Line of business'),
      this.createSection('Date', 'Date'),
      this.createSection('Lag time', 'Lag Time'),
      this.createSection('Policy and plan', 'Policy and plan'),
      this.createSection('Agency', 'Agency'),
      this.createSection('Financial', 'Financial'),
      this.createSection('State', 'State'),
      this.createSection('Claim flags', 'Flag'),
      this.createSection('Work status', 'Work Status'),
      this.createSection('Client field', 'Client Field'),
      this.createSection('Loss codes', 'Loss'),
      this.createSection('Examiner', 'Examiner'),
      this.createSection('Claim number', 'Claim Number'),
    ];

    // Filter sections based on security
    const visibility = this.filterGroupVisibility();
    const filteredSections = allSections.filter((section) => {
      switch (section.type) {
        case 'Location':
          return visibility?.showLocations ?? true;
        case 'Financial':
          return visibility?.showFinancial ?? true;
        case 'Flag':
          return visibility?.showClaimFlags ?? true;
        case 'Work Status':
          return visibility?.showWorkStatus ?? true;
        case 'Client Field':
          return visibility?.showClientField ?? true;
        case 'Loss':
          return visibility?.showLossCodes ?? true;
        case 'Examiner':
          return visibility?.showExaminer ?? true;
        case 'Policy and plan':
          return !(visibility?.isPolicyPlanRestricted ?? false);
        case 'Agency':
          return visibility?.showAgency ?? false;
        default:
          return true;
      }
    });

    this.sections.set(filteredSections);

    this.claimPolicyVisibility = claimPolicyVisibility;
    const shouldShowClaimPolicy =
      this.claimPolicyVisibility?.showPolicy ||
      this.claimPolicyVisibility?.showExt ||
      this.claimPolicyVisibility?.showLegacy;

    this.toggleDynamicSection(
      shouldShowClaimPolicy,
      'Claim Policy',
      'Claim policy',
      'Agency',
    );

    if (!this.isCopyMode) {
      const matchedFilter = this.filtersService
        .userFilters()
        .find((f) => f.filterId === this.filterDetail?.filterId);
      if (matchedFilter) {
        this.fg.controls.filterName.setValue(matchedFilter.filterName);
      }
    }

    this.initialized = true;
  }

  createSection(label: string, type: FilterSectionType): FilterSection {
    const section = new FilterSection(label, type);
    if (this.filterDetail) {
      section.values = this.filterDetail.criteria.filter(
        (x) => x.filterSection === type,
      );
    }

    section.enabled.set(section.values.length > 0);
    section.count.set(section.values.length);
    return section;
  }

  validCriteria(): boolean {
    for (const section of this.sections()) {
      if (section.enabled()) {
        if (section.criteria && !section.criteria.valid()) {
          return false;
        }
      }
    }

    return true;
  }

  toggleEnabled(section: FilterSection) {
    const enabled = !section.enabled();

    if (!enabled && section.count() > 0) {
      const labelLower = section.label.toLowerCase();
      this.confirm.show({
        label: `Delete ${labelLower}`,
        message: `Are you sure you want to delete ${labelLower}?`,
        width: '35rem',
        confirm: async () => {
          this.setEnabled(enabled, section);
        },
      });
    } else {
      this.setEnabled(enabled, section);
      if (enabled) {
        this.dispose.timeouts = setTimeout(() => {
          const id = section.label.toLowerCase().split(' ').join('-');
          document
            .getElementById(id)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }

  setEnabled(enabled: boolean, section: FilterSection) {
    section.enabled.set(enabled);
    if (!enabled) {
      // clear the count and dereference the criteria.
      section.count.set(null);
      section.criteria = null;
      section.values = [];
    }
  }

  resetAllSections() {
    for (const section of this.sections()) {
      if (section.enabled()) {
        this.setEnabled(false, section);
      }
    }
  }

  getCriteria(): FilterCriteria[] {
    const criteria: FilterCriteria[] = [];
    for (const section of this.sections()) {
      if (section.enabled() && section.criteria) {
        criteria.push(...section.criteria.getCriteria());
      }
    }

    return criteria;
  }

  private filterNameExistsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value?.trim()?.toUpperCase();
      if (!name) return null;

      const excludeId = !this.isCopyMode ? this.filterDetail?.filterId : null;

      const exists = this.filtersService
        .userFilters()
        .some(
          (f) =>
            f.filterName?.trim()?.toUpperCase() === name &&
            f.filterId !== excludeId,
        );

      return exists
        ? { filterNameExists: 'A filter with this name already exists' }
        : null;
    };
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async updateDynamicSections(contractNumber: string) {
    const [visibility, claimPolicyVisibility] = await Promise.all([
      this.api.filtersCriteria.getFilterGroupVisibility(contractNumber),
      this.api.filtersCriteria.getClaimPolicyVisibility(contractNumber),
    ]);

    this.filterGroupVisibility.set(visibility);
    this.toggleDynamicSection(
      visibility?.showAgency ?? false,
      'Agency',
      'Agency',
      'Policy and plan',
    );

    this.claimPolicyVisibility = claimPolicyVisibility;
    const shouldShowClaimPolicy =
      claimPolicyVisibility?.showPolicy ||
      claimPolicyVisibility?.showExt ||
      claimPolicyVisibility?.showLegacy;
    this.toggleDynamicSection(
      shouldShowClaimPolicy,
      'Claim Policy',
      'Claim policy',
      'Agency',
    );
  }

  private toggleDynamicSection(
    shouldShow: boolean,
    type: FilterSectionType,
    label: string,
    insertAfterType: FilterSectionType,
  ) {
    const existingIndex = this.sections().findIndex((s) => s.type === type);

    if (shouldShow && existingIndex < 0) {
      this.sections.update((arr) => {
        const afterIndex = arr.findIndex((s) => s.type === insertAfterType);
        const newSection = this.createSection(label, type);

        if (afterIndex >= 0) {
          return [
            ...arr.slice(0, afterIndex + 1),
            newSection,
            ...arr.slice(afterIndex + 1),
          ];
        }
        return [...arr, newSection];
      });
    } else if (!shouldShow && existingIndex >= 0) {
      this.sections.update((arr) => arr.filter((s) => s.type !== type));
    }
  }
}

export interface ICriteria {
  valid(): boolean;

  getCriteria(): FilterCriteria[];
}

export class FilterSection {
  constructor(
    public label: string,
    public type: FilterSectionType = null,

    public enabled = signal<boolean>(false),
    public count = signal<number>(null),
    public criteria: ICriteria = null,

    public values: FilterCriteria[] = [],
  ) {}
}
